import { hzToMidi } from '../util/note';
import type { CalibrationStep, StepCapture } from './types';

const DEFAULT_MIDI_TOLERANCE = 0.75;
const DEFAULT_PEAK_LIMIT = 6;

export interface ChordCaptureEvidence {
  chordFrames: number;
  supportCounts: Map<number, number>;
  supportedDistinct: number;
}

const uniqueRoundedMidis = (frame: StepCapture['peakFrames'][number], peakLimit: number) => {
  const midis = new Set<number>();
  for (const peak of frame.peaks.slice(0, peakLimit)) {
    const midi = Math.round(hzToMidi(peak.hz));
    if (midi >= 0 && midi <= 127) midis.add(midi);
  }
  return Array.from(midis);
};

const matchedExpectedMidis = (
  step: CalibrationStep,
  frame: StepCapture['peakFrames'][number],
  midiTolerance: number,
  peakLimit: number,
) => {
  const expectedMidis = step.expectedMidi ?? [];
  if (expectedMidis.length === 0) return uniqueRoundedMidis(frame, peakLimit);

  const peakMidis = frame.peaks.slice(0, peakLimit).map((peak) => hzToMidi(peak.hz));
  return expectedMidis.filter((expectedMidi) =>
    peakMidis.some((peakMidi) => Math.abs(peakMidi - expectedMidi) <= midiTolerance),
  );
};

export const summarizeChordCaptureEvidence = (
  step: CalibrationStep,
  frames: StepCapture['peakFrames'],
  options: {
    midiTolerance?: number;
    peakLimit?: number;
    minimumSupportFrames?: number;
  } = {},
): ChordCaptureEvidence => {
  const midiTolerance = options.midiTolerance ?? DEFAULT_MIDI_TOLERANCE;
  const peakLimit = options.peakLimit ?? DEFAULT_PEAK_LIMIT;
  const minimumSupportFrames = options.minimumSupportFrames ?? 3;
  const requiredDistinct = step.minDistinctMidis ?? 2;
  const supportCounts = new Map<number, number>();
  let chordFrames = 0;

  for (const frame of frames) {
    const matchedMidis = matchedExpectedMidis(step, frame, midiTolerance, peakLimit);
    if (matchedMidis.length >= requiredDistinct) chordFrames += 1;
    for (const midi of matchedMidis) {
      supportCounts.set(midi, (supportCounts.get(midi) ?? 0) + 1);
    }
  }

  const supportedDistinct = Array.from(supportCounts.values()).filter(
    (count) => count >= minimumSupportFrames,
  ).length;

  return { chordFrames, supportCounts, supportedDistinct };
};
